import React, { useEffect, useState, useCallback } from 'react';
import { When } from 'react-if';
import {
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Box,
    Chip,
    Tooltip,
    IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { ReportTypes } from '../../../types';
import { ReportService } from '../../../services/report.service';
import { PaginatorFactory, PaginationFactory } from '../../../factory';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import {
    FSR_CATEGORIES,
    getFsrCategoryLabel,
    getFsrCreatedBy,
} from '../../../helpers/fieldSafetyReports';
import AreYouSureModal from '../../are-you-sure-modal';
import NoData from '../../no-data';
import Loading from '../../loading';
import {
    FieldSafetyReportsFilters,
    FieldSafetyReportsFilterValues,
} from './field-safety-reports-filters';

const COLUMNS: { headerName: string; width?: string | number }[] = [
    { headerName: 'Category' },
    { headerName: 'Mobile visibility' },
    { headerName: 'Created by' },
    { headerName: 'Created' },
    { headerName: 'Address' },
    { headerName: '' },
];

interface Props {
    onViewReport?: (report: ReportTypes.Model) => void;
}

export const FieldSafetyReportsList: React.FC<Props> = ({ onViewReport }) => {
    const theme = useTheme();
    const canDelete = permissionCheck(customerPermissionFlags.TEAM_LEADER);

    const [items, setItems] = useState<ReportTypes.Model[]>([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState<PaginatorFactory<ReportTypes.Model>>({
        page: 0,
        perPage: 25,
    });
    const [filters, setFilters] = useState<FieldSafetyReportsFilterValues>({ categories: [] });
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [selectedReport, setSelectedReport] = useState<ReportTypes.Model | null>(null);
    const [deleteModal, setDeleteModal] = useState(false);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const serverCategories: readonly string[] =
                filters.categories.length > 0 ? filters.categories : FSR_CATEGORIES;
            const filter: Record<string, unknown> = {
                reportType: { $in: serverCategories },
            };
            if (filters.createdBy) {
                const escaped = filters.createdBy.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                filter.sharedBy = { $regex: escaped, $options: 'i' };
            }

            const result = (await ReportService.fetchMany(
                {
                    page: pagination.page + 1,
                    perPage: pagination.perPage,
                    filter,
                },
                undefined,
                true
            )) as PaginationFactory<ReportTypes.Model> | void;

            const selected = new Set<string>(filters.categories);
            const allowed = new Set<string>(FSR_CATEGORIES);
            const rangeStart = filters.fromDate ? dayjs(filters.fromDate).startOf('day') : null;
            const rangeEnd = filters.toDate ? dayjs(filters.toDate).endOf('day') : null;
            const createdByNeedle = filters.createdBy?.trim().toLowerCase() ?? '';

            const rows = (result?.items ?? []).filter((r) => {
                if (!allowed.has(r.reportType)) return false;
                if (selected.size > 0 && !selected.has(r.reportType)) return false;
                if (rangeStart || rangeEnd) {
                    const created = dayjs(r.createdAt);
                    if (rangeStart && created.isBefore(rangeStart)) return false;
                    if (rangeEnd && created.isAfter(rangeEnd)) return false;
                }
                if (createdByNeedle && !(r.sharedBy ?? '').toLowerCase().includes(createdByNeedle)) {
                    return false;
                }
                return true;
            });
            setItems(rows);
            // pom-2-api /reports/getMany doesn't translate fromDate/toDate into a Mongo
            // createdAt range, so date filtering is client-side and the server total
            // ignores it. Fall back to the post-filter row count to keep pagination
            // honest (single page) until the API gains date-range support.
            const dateFilterActive = !!(filters.fromDate || filters.toDate);
            setTotal(dateFilterActive ? rows.length : (result?.total ?? rows.length));
        } catch (e) {
            console.error('[FieldSafetyReportsList] fetch failed', e);
            setItems([]);
            setTotal(0);
            toast('Failed to load field safety reports', { autoClose: 2500, type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.perPage, filters]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleFilter = useCallback((next: FieldSafetyReportsFilterValues) => {
        setPagination((pag) => ({ ...pag, page: 0 }));
        setFilters(next);
    }, []);

    const onDelete = async () => {
        if (!selectedReport) return;
        setDeleting(true);
        await ReportService.destroy([selectedReport.id], (isValid, msg) => {
            setDeleting(false);
            setDeleteModal(false);
            if (!isValid) {
                toast(msg ?? 'Error deleting report', { autoClose: 1500, type: 'error' });
                return;
            }
            toast('Field safety report deleted', { autoClose: 1500, type: 'success' });
            setSelectedReport(null);
            fetchReports();
        });
    };

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <FieldSafetyReportsFilters onFilter={handleFilter} />
            </Box>
            <Card sx={{ mt: 1 }}>
                <Box sx={{ p: 2 }}>
                    <Typography fontSize={16} fontWeight={500}>
                        Field Safety Reports
                    </Typography>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map((column, index) => (
                                    <TableCell key={index}>
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                            {column.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading}>
                            <TableBody>
                                {items.map((report) => (
                                    <TableRow
                                        key={report.id}
                                        onClick={() => onViewReport?.(report)}
                                        sx={{
                                            cursor: onViewReport ? 'pointer' : 'default',
                                            ':hover': { backgroundColor: alpha(theme.palette.grey[500], 0.08) },
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={getFsrCategoryLabel(report.reportType)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                icon={<VisibilityIcon />}
                                                label="Visible on mobile"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {getFsrCreatedBy(report.sharedBy)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {dayjs(report.createdAt).format('MM/DD/YYYY hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {report.address || report.formatedAddress || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {canDelete && (
                                                <Tooltip title="Delete report">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedReport(report);
                                                            setDeleteModal(true);
                                                        }}
                                                    >
                                                        <DeleteForeverIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                    <When condition={!loading && items.length === 0}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title="No field safety reports found"
                            description="Reports filed by caregivers in the field will appear here."
                        />
                    </When>
                    <When condition={loading}>
                        <Loading />
                    </When>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={pagination.perPage}
                    page={pagination.page}
                    onPageChange={(_e, p) => setPagination((pag) => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) =>
                        setPagination((pag) => ({
                            ...pag,
                            page: 0,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>
            <AreYouSureModal
                title="Delete this field safety report?"
                description="This will permanently remove the record. Caregivers will no longer be notified when they enter the area."
                onHide={() => setDeleteModal(false)}
                loading={deleting}
                onConfirm={onDelete}
                visible={deleteModal}
            />
        </>
    );
};

export default FieldSafetyReportsList;
